// A record's address in the design's words (impire-hq 02-DESIGN/11-observe/
// 03-the-observability-solution.md § the console: "each the address in the
// design's words"): the subjects a row of the verb table names
// (02-DESIGN/15-terminal-client/03-the-verb-table.md), each with the
// console's label for it (02-DESIGN/03-console/01-the-console.md § the
// words), in their tokenless client form. The subjects are the wire's, so
// they live here; what names a workload or a solution is the caller's.

/** ACCOUNT is a NATS account's public key, the account slot of a platform form. */
const ACCOUNT = /^A[A-Z2-7]{55}$/;

/** tokensOf is an address's tokens, an account slot after the family dropped. */
export function tokensOf(address: string): string[] {
  const toks = address.split(".").filter((t) => t !== "");
  if (toks.length > 1 && ACCOUNT.test(toks[1] ?? "")) {
    toks.splice(1, 1);
  }
  return toks;
}

/** Namer names what an address's tokens identify: a workload by its id, a solution by its name. */
export interface Namer {
  workload: (id: string) => string;
  solution: (name: string) => string;
}

type Say = (v: readonly string[], n: Namer) => string;

function wl(id: string | undefined, n: Namer): string {
  return n.workload(id ?? "");
}

function sol(name: string | undefined, n: Namer): string {
  return n.solution(name ?? "");
}

/**
 * WORDS are the subjects a row of the verb table names, in its words
 * (02-DESIGN/15-terminal-client/03-the-verb-table.md, the console's labels
 * of 02-DESIGN/03-console/01-the-console.md § the words). A pattern's `*`
 * is one token, handed to the words in order; a trailing `>` is the rest.
 * A subject no row names is shown as itself.
 */
const WORDS: readonly (readonly [string, Say])[] = [
  ["identity.carrier", () => "Connect"],
  ["identity.keys", () => "Connect"],
  ["identity.apps.list", () => "List apps"],
  ["identity.apps.get", () => "Read an app"],
  ["identity.apps.register", () => "Connect an app"],
  ["identity.apps.rotate", () => "New key for an app"],
  ["identity.apps.revoke", () => "Disconnect an app"],
  ["identity.apps.restore", () => "Restore an app"],
  ["identity.rows.list", () => "List access rules"],
  ["identity.rows.put", () => "Save an access rule"],
  ["identity.rows.delete", () => "Remove an access rule"],
  ["identity.members.add", () => "Add a person"],
  ["identity.members.invite", () => "Send an invite again"],
  ["identity.kick", () => "Disconnect a person"],
  ["identity.sign-out-all", () => "Sign everyone out"],
  ["identity.signins.list", () => "List sign-ins"],
  ["identity.tenant.get", () => "Read the workspace"],
  ["identity.settings.put", () => "Change the workspace's name"],
  ["identity.provider.get", () => "Read the sign-in"],
  ["identity.provider.put", () => "Set up the sign-in"],
  ["identity.provider.delete", () => "Remove the sign-in"],
  ["identity.provider.use", () => "Switch the sign-in"],
  ["identity.escrow.status", () => "Read the recovery shares"],
  ["identity.escrow.collect", () => "Collect a recovery share"],
  ["identity.escrow.resplit", () => "New recovery shares"],
  ["identity.escrow.present", () => "Present a recovery share"],
  ["identity.escrow.child.*.status", (v) => `Read ${v[0] ?? ""}'s recovery shares`],
  ["identity.escrow.child.*.collect", (v) => `Collect the recovery share for ${v[0] ?? ""}`],
  ["identity.escrow.child.*.present", (v) => `Present the recovery share for ${v[0] ?? ""}`],
  ["identity.children.list", () => "List workspaces"],
  ["identity.children.create", () => "New workspace"],
  ["workloads.list", () => "List workloads"],
  ["workloads.register", () => "Add a workload"],
  ["workloads.get.*", (v, n) => `Read ${wl(v[0], n)}`],
  ["workloads.update.*", (v, n) => `Change ${wl(v[0], n)}`],
  ["workloads.retire.*", (v, n) => `Remove ${wl(v[0], n)}`],
  ["workloads.suspend.*", (v, n) => `Stop ${wl(v[0], n)}`],
  ["workloads.resume.*", (v, n) => `Start ${wl(v[0], n)}`],
  ["workloads.records.*", (v, n) => `Read ${wl(v[0], n)}'s output`],
  ["config.get.workload.*.*", (v, n) => `Read ${wl(v[0], n)}'s ${v[1] ?? ""}`],
  ["config.set.workload.*.*", (v, n) => `Save ${wl(v[0], n)}'s ${v[1] ?? ""}`],
  ["config.delete.workload.*.*", (v, n) => `Delete ${wl(v[0], n)}'s ${v[1] ?? ""}`],
  ["config.list.workload.*", (v, n) => `List ${wl(v[0], n)}'s settings`],
  ["config.watch.workload.>", (v, n) => `Follow ${wl(v[0], n)}'s settings`],
  ["config.get.solution.*.*", (v, n) => `Read ${sol(v[0], n)}'s ${v[1] ?? ""}`],
  ["config.set.solution.*.*", (v, n) => `Save ${sol(v[0], n)}'s ${v[1] ?? ""}`],
  ["config.delete.solution.*.*", (v, n) => `Delete ${sol(v[0], n)}'s ${v[1] ?? ""}`],
  ["solutions.installed.list", () => "List solutions"],
  ["solutions.installed.get.*", (v, n) => `Read ${sol(v[0], n)}`],
  ["solutions.catalogue.list", () => "Read the catalogue"],
  ["solutions.catalogue.get.*", (v) => `Read ${v[0] ?? ""} in the catalogue`],
  ["solutions.install.*", (v) => `Install ${v[0] ?? ""}`],
  ["solutions.upgrade.*", (v, n) => `Update ${sol(v[0], n)}`],
  ["solutions.remove.*", (v, n) => `Remove ${sol(v[0], n)}`],
  ["guard.rules.list", () => "List guardrails"],
  ["guard.rules.get.*", () => "Read a guardrail"],
  ["guard.rules.set.*", () => "Save a guardrail"],
  ["guard.rules.delete.*", () => "Delete a guardrail"],
  ["guard.escalations.*.list", () => "List requests waiting"],
  ["guard.escalations.*.resolve.*", () => "Approve or deny a request"],
  ["state.store.list", () => "List stores"],
  ["state.store.create.*", (v) => `Create store ${v[0] ?? ""}`],
  ["state.store.get.*", (v) => `Read store ${v[0] ?? ""}`],
  ["state.store.update.*", (v) => `Change store ${v[0] ?? ""}`],
  ["state.store.delete.*", (v) => `Delete store ${v[0] ?? ""}`],
  ["vault.list.>", () => "List secrets"],
  ["vault.store.>", () => "Store a secret"],
  ["vault.delete.>", () => "Delete a secret"],
];

function matchOf(pattern: string, toks: readonly string[]): string[] | undefined {
  const p = pattern.split(".");
  const vals: string[] = [];
  for (let i = 0; i < p.length; i++) {
    const want = p[i];
    if (want === ">") {
      if (toks.length <= i) {
        return undefined;
      }
      vals.push(...toks.slice(i));
      return vals;
    }
    const got = toks[i];
    if (got === undefined) {
      return undefined;
    }
    if (want === "*") {
      vals.push(got);
    } else if (want !== got) {
      return undefined;
    }
  }
  return toks.length === p.length ? vals : undefined;
}

/**
 * addressWords is a record's address in the design's words — *Stop
 * support-bot*, *Chat with gpt-review* — or undefined where no row names it,
 * which the list then shows as the subject itself. An address without a dot
 * is already words: *signed in*, *recovery opened*.
 */
export function addressWords(address: string, n: Namer): string | undefined {
  if (address === "") {
    return undefined;
  }
  if (!address.includes(".")) {
    return address;
  }
  const toks = tokensOf(address);
  if (toks[0] === "workload" && toks.length >= 3) {
    const name = wl(toks[1], n);
    const rest = toks.slice(2);
    if (rest[0] === "infer") {
      const op = rest.slice(1).join(".");
      return op === "chat" || op === "" ? `Chat with ${name}` : `Ask ${name} (${op})`;
    }
    if (rest[0] === "tool") {
      const t = rest.slice(1).join(".");
      return t === "_exchange" ? `Link ${name}` : t === "" ? `Use ${name}` : `Use ${t} on ${name}`;
    }
    return `${rest.join(".")} on ${name}`;
  }
  for (const [pattern, say] of WORDS) {
    const vals = matchOf(pattern, toks);
    if (vals !== undefined) {
      return say(vals, n);
    }
  }
  return undefined;
}
